import type { Cube } from '@cubejs-client/core'

import {
  parseDocumentWidget,
  type ChartWidget,
  type DashboardWidget,
  type ListWidget,
  type ReportFilterDef,
} from '@/lib/cube-report'
import { relativeRange, shiftDateInput } from '@/lib/format'
import { periodRange, shiftPeriod } from '@/lib/period'
import { messages } from '@/lib/messages'
import { widgetNodeSchemas } from '@/lib/report-document'

import type { WidgetNodeName } from './data-source'

/*
 * Belge widget düğümü (öznitelikler = `widgetNodeSchemas`) -> pano parçası (`DashboardWidget`).
 * Canlı önizleme de dönem kopyasının üretimi (D7) de aynı dönüşümü kullanır.
 */

export type WidgetBuild =
  | { ok: true; widget: DashboardWidget; /** `reportList` satır sınırı. */ limit?: number }
  | { ok: false; reason: 'forbidden' }
  | { ok: false; reason: 'invalid'; message: string }

/**
 * Grafiğin dönemden bağımsız göreli aralığı: `-12m` = seçili dönemin SONUNDA biten son 12 ay.
 * Dönem filtresi yoksa bugüne göre (`relativeRange`). Gün birimi: dönem sonuna kadar son N gün.
 */
export function anchoredRange(token: string, period: string | undefined): [string, string] {
  const match = /^-(\d+)([dmy])$/.exec(token)
  if (!period || !match) {
    const { from, to } = relativeRange(token)
    return [from, to]
  }
  const amount = Number(match[1])
  const to = periodRange(period)[1]
  if (match[2] === 'd') return [shiftDateInput(to, 1 - amount), to]
  const months = match[2] === 'y' ? amount * 12 : amount
  return [`${shiftPeriod(period, 1 - months)}-01`, to]
}

function qualify(widget: DashboardWidget, short: string): string {
  const name = `${widget.view}.${short}`
  if (!widget.fields.has(name)) throw new Error(messages.documents.editor.memberMissing(name))
  return name
}

export function buildDocumentWidget(
  type: WidgetNodeName,
  attrs: Record<string, unknown>,
  views: ReadonlyMap<string, Cube>,
  pageFilters: ReportFilterDef[],
  period: string | undefined,
): WidgetBuild {
  let widget: DashboardWidget | undefined
  let limit: number | undefined
  let where: { member: string; values: string[] }[] = []
  let range: string | undefined
  try {
    switch (type) {
      case 'reportKpi': {
        const parsed = widgetNodeSchemas.reportKpi.shape.attrs.parse(attrs)
        widget = parseDocumentWidget({ ...parsed, type: 'kpi' }, 'doc', pageFilters, views)
        break
      }
      case 'reportTable': {
        const parsed = widgetNodeSchemas.reportTable.shape.attrs.parse(attrs)
        widget = parseDocumentWidget({ ...parsed, type: 'table' }, 'doc', pageFilters, views)
        break
      }
      case 'reportList': {
        const parsed = widgetNodeSchemas.reportList.shape.attrs.parse(attrs)
        widget = parseDocumentWidget({ ...parsed, type: 'list' }, 'doc', pageFilters, views)
        ;({ where, limit } = parsed)
        break
      }
      case 'reportChart': {
        const parsed = widgetNodeSchemas.reportChart.shape.attrs.parse(attrs)
        widget = parseDocumentWidget({ ...parsed, type: 'chart' }, 'doc', pageFilters, views)
        range = parsed.range
        break
      }
    }
    if (!widget) return { ok: false, reason: 'forbidden' }
    // Belge parçaları etkileşimsizdir: çapraz filtre yayılmaz/alınmaz.
    widget.crossFilter = { emit: false, receive: false }
    if (widget.type === 'list') {
      const list: ListWidget = widget
      list.where = where.map((item) => ({
        member: qualify(list, item.member),
        values: item.values,
      }))
    }
    if (widget.type === 'chart' && range) {
      const chart: ChartWidget = widget
      chart.range = anchoredRange(range, period)
    }
    return { ok: true, widget, limit }
  } catch (error) {
    // Zod hatası (eksik view/üye) ya da `qualify` / meta üye hatası: bloğun içinde gösterilir.
    return { ok: false, reason: 'invalid', message: firstIssue(error) }
  }
}

function firstIssue(error: unknown): string {
  const issues = (error as { issues?: { message: string; path: PropertyKey[] }[] }).issues
  if (issues?.[0]) return `${issues[0].path.join('.')}: ${issues[0].message}`
  return error instanceof Error ? error.message : String(error)
}
